import React, { Component } from 'react';
import { getUnifiedCurrentUser, getMusicEngineTrending } from '../../api/api';
import PageSpinner from 'components/Spinner/PageSpinner';
import Header from 'components/Header/Header';
import Navigation from 'navigation/navigation';
import Style from './Trending.module.css';
import PodcastStyle from '../podcast/Podcast.module.css';
import { logEventPagesViewed } from 'utils/analytics';

class Trending extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: null,
      podcastFeed: [],
      trendingChannels: [], // transformed channels with podcasts
      activeTab: 0,
      loading: true,
      loadingText: '',
      successMessage: '',
      errMessage: '',
      focusNext: false,
      focusPrevious: false
    };

    this.nextButtonRef = React.createRef();
    this.previousButtonRef = React.createRef();
  }

  async componentDidMount() {
    // Track page view (non-blocking)
    logEventPagesViewed(null, window.location.href, 'trending_page', 'Trending').catch(() => {});
    
    this.loadOfflineData();
    await this.getUser();
    document.addEventListener('keydown', this.onKeyDown);
    this.unblock = this.props.history.block((location, action) => {
      return action === 'POP' ? window.close() : undefined;
    });
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.unblock) {
      this.unblock();
    }
  }

  getUser = async () => {
    try {
      const { user } = await getUnifiedCurrentUser();
      if (user) {
        this.setState({ user: user });
      }
      await this.loadTrending();
    } catch (error) {
    }
  };

  getData = async (isPrevious = false) => {
    try {
      await this.loadTrending();
      // Analytics already tracked in componentDidMount
    } catch (error) {
      this.loadOfflineData();
      this.setState((prevState) => ({
        loading: false,
        loadingText: '',
        errMessage: error || 'Something Went Wrong'
      }));
    }
  };

  loadTrending = async () => {
    try {
      const resp = await getMusicEngineTrending();
      const channels = Array.isArray(resp?.channels) ? resp.channels : (Array.isArray(resp?.data?.channels) ? resp.data.channels : []);

      const transformedChannels = channels.map((channel, index) => {
        const items = (channel && channel.objects && Array.isArray(channel.objects.data)) ? channel.objects.data : [];
        const podcasts = items.map((p, idx) => ({
          id: p.id || p._id || idx,
          title: p.title || p.name || 'Podcast',
          author: (p.artist && p.artist.name) || p.author || p.creator || p.artist_name || 'Unknown',
          image: p.artwork_url || p.image || p.artwork || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1tYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+UG9kY2FzdDwvdGV4dD4KPC9zdmc+'
        }));
        return {
          id: channel.id || index,
          title: channel.title || channel.name || 'Channel',
          description: channel.description || '',
          slug: channel.alt_name || channel.slug || (channel.title || '').toLowerCase().replace(/\s+/g, '-'),
          podcasts,
        };
      });

      this.setState({ trendingChannels: transformedChannels, loading: false, loadingText: '' });
    } catch (error) {
      throw error;
    }
  };

  handleViewAllClick = (sectionTitle) => {
    const channel = (this.state.trendingChannels || []).find(ch => ch.title === sectionTitle);
    const podcasts = (channel && Array.isArray(channel.podcasts)) ? channel.podcasts : [];
    this.props.history.push({ pathname: '/podcastlist', state: { title: sectionTitle, podcasts } });
  };

  loadOfflineData = () => {
    const podcastFeedData = localStorage.getItem('podcastFeedData');
    const podcastFeeds = podcastFeedData ? JSON.parse(podcastFeedData) : null;
    if (podcastFeeds) {
      this.setState({
        podcastFeed: podcastFeeds,
        errMessage: ''
      });
    }
  };

  updateLoadingText = () => {
    this.setState((prevState) => {
      const newText = prevState.loadingText === '...' ? '.' : prevState.loadingText + '.';
      return { loadingText: newText };
    });
  };

  loadNext = async () => {
    if (this.state.loading) {
      return;
    }
    if (!this.state.nextPage) {
      return;
    }
    if (this.state.nextLoading || this.state.previousLoading) {
      return;
    }
    this.setState({
      nextLoading: true,
    }, async () => {
      await this.getData();
    });
  };

  loadPrevious = async () => {
    if (this.state.loading) {
      return;
    }
    if (this.state.nextLoading || this.state.previousLoading) {
      return;
    }
    let nextSkip = this.state.nextSkip;
    if (!nextSkip || nextSkip <= 50) {
      return;
    }
    this.setState({
      previousLoading: true,
    }, async () => {
      await this.getData(true);
    });
  };

  scrollToCardById = (id) => {
    const divElement = document.getElementById(`podcastCard-${id}`);
    if (divElement) {
      const rect = divElement.getBoundingClientRect();
      const isInViewPort = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!isInViewPort) {
        const targetScrollTop = divElement.offsetTop - (window.innerHeight * 0.5) + (rect.height / 2);
        setTimeout(() => {
          window.scroll({
            top: targetScrollTop,
            left: 0,
            behavior: 'smooth'
          });
        });
      }
    }
  };

  scrollToBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    });
  };

  onKeyDown = (e) => {
    // Ignore when typing in form fields
    const target = e && e.target;
    const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
    const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
    if (isFormField) {
      return;
    }
    return;
    const { activeTab, podcastFeed, nextPage, nextSkip, focusNext, focusPrevious } = this.state;
    const maxIndex = podcastFeed.length - 1;
    if (!e.key) {
      return;
    }
    if (e.key === 'ArrowRight') {
      if (activeTab % 2 === 0 && activeTab < maxIndex) {
        this.setState({ activeTab: activeTab + 1 }, () => {
          this.scrollToCardById(this.state.activeTab);
        });
      }
    } else if (e.key === 'ArrowLeft') {
      if (activeTab % 2 !== 0) {
        this.setState({ activeTab: activeTab - 1 }, () => {
          this.scrollToCardById(this.state.activeTab);
        });
      }
    } else if (e.key === 'ArrowDown') {
      if (this.nextButtonRef.current) {
        this.nextButtonRef.current.blur();
      }
      if (this.previousButtonRef.current) {
        this.previousButtonRef.current.blur();
      }
      if (activeTab < maxIndex) {
        this.setState((prev) => {
          const newIndex = Math.min(prev.activeTab + 2, maxIndex);
          setTimeout(() => { this.scrollToCardById(newIndex); });
          return { activeTab: newIndex };
        });
      } else {
        if (nextSkip > 50) {
          if (focusPrevious || nextPage) {
            this.setState({ focusNext: true, focusPrevious: false }, () => {
              if (this.nextButtonRef.current) {
                this.nextButtonRef.current.focus();
              }
            });
          } else if (focusNext || !nextPage) {
            this.setState({ focusPrevious: true, focusNext: false }, () => {
              if (this.previousButtonRef.current) {
                this.previousButtonRef.current.focus();
              }
            });
          }
          this.scrollToBottom();
        } else {
          this.setState({ focusNext: true, focusPrevious: false }, () => {
            if (this.nextButtonRef.current) {
              this.nextButtonRef.current.focus();
            }
          });
          this.scrollToBottom();
        }
      }
    } else if (e.key === 'ArrowUp') {
      if (this.nextButtonRef.current) {
        this.nextButtonRef.current.blur();
      }
      if (this.previousButtonRef.current) {
        this.previousButtonRef.current.blur();
      }
      if (focusNext && nextSkip > 50) {
        this.setState({ focusPrevious: true, focusNext: false }, () => {
          if (this.previousButtonRef.current) {
            this.previousButtonRef.current.focus();
          }
        });
        this.scrollToBottom();
      } else if (activeTab > 0) {
        this.setState({ activeTab: (focusNext || focusPrevious) ? activeTab : activeTab - 2, focusPrevious: false, focusNext: false }, () => {
          this.scrollToCardById(this.state.activeTab);
        });
      }
    } else if (e.key === 'SoftLeft' || e.key === 'Enter') {
      if (focusNext) {
        this.loadNext();
      } else if (focusPrevious) {
        this.loadPrevious();
      } else {
        const data = podcastFeed[activeTab];
        this.props.history.push(`/podcastdetail/${data._id}?`);
      }
    } else if (e.key === 'SoftRight' || e.key === 'Backspace') {
      this.props.history.goBack();
    }
  };

  render() {
    const { podcastFeed, loading, successMessage, errMessage, trendingChannels } = this.state;
    return (
      <div>
        <Header title='Trending' />
        {loading ? (
          <PageSpinner />
        ) : (
          <>
            {successMessage.length ? <span style={{ marginTop: 10, color: 'green', fontSize: '0.8rem' }}>{successMessage}</span> : null}
            {errMessage.length ? <span style={{ marginTop: 10, color: 'red', fontSize: '0.8rem' }}>{errMessage}</span> : null}
            {(trendingChannels || []).map((channel, index) => (
              <div key={channel.id || index} className={PodcastStyle.contentSection}>
                <div className={PodcastStyle.divTitleSection}>
                  <div className={PodcastStyle.divTitleRow}>{channel.title}</div>
                  {channel.description && (
                    <div className={PodcastStyle.divSubtitleRow}>{channel.description}</div>
                  )}
                </div>
                <div className={PodcastStyle.itemsContainer}>
                  {channel.podcasts && channel.podcasts.slice(0, 5).map((podcast, podcastIndex) => (
                    <div
                      key={podcast.id || podcastIndex}
                      className={PodcastStyle.itemCard}
                      onClick={() => this.props.history.push(`/podcastdetail/${podcast.id}`)}
                    >
                      <img
                        src={podcast.image}
                        alt={podcast.title}
                        className={PodcastStyle.itemImage}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjNDUxZTUzIi8+Cjx0ZXh0IHg9IjYwIiB5PSI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGNlIiBkeT0iLjNlbSI+RmFpbGVkPC90ZXh0Pgo8L3N2Zz4=';
                        }}
                      />
                      <div className={PodcastStyle.itemContent}>
                        <div className={PodcastStyle.itemName}>{podcast.title}</div>
                        <div className={PodcastStyle.itemAuthor}>by {podcast.author}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {channel.podcasts && channel.podcasts.length > 5 && (
                  <div style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px', marginBottom: '20px' }}>
                    <span className={PodcastStyle.button} style={{ cursor: 'pointer' }} onClick={() => this.handleViewAllClick(channel.title)}>
                      See All ({channel.podcasts.length})
                    </span>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
        <div style={{ height: '60px' }}></div>
        <Navigation active='Trending' />
      </div>
    );
  }
}

export default Trending;
